import Debug from '../../helpers/debug.js';
import ora from 'ora';
import { readFileSync, existsSync } from 'fs';
import Table from 'cli-table';
import { pr, themed, printJSON } from '../../helpers/print.js';
import { retriggerJob, getJobStatus } from '../../helpers/api.js';

const debug = (...args) => {
  const debug = Debug('commands:jobs:bulkRetrigger');
  debug(...args);
};

const command = 'bulk-retrigger';

const desc = 'Retrigger jobs in bulk';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('file', {
      describe: 'Path to JSON file containing array of jobRoots to retrigger',
      type: 'string',
      alias: ['f'],
      demandOption: true,
    })
    .demandOption('file', 'Path to JSON file is required. Example: -f ./jobRoots.json');
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, file } = argv;
  const { clientApiBaseUri } = hg;

  let message;
  let spinner = ora();
  try {
    // 1. Read and validate JSON file
    message = themed(`Reading jobRoots file: ${themed(file, 'info')}`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    if (!existsSync(file)) {
      throw new Error(`File not found: ${file}`);
    }

    const fileContent = readFileSync(file, { encoding: 'utf-8' });
    const jobRoots = JSON.parse(fileContent);

    if (!Array.isArray(jobRoots)) {
      throw new Error('JSON file must contain an array of jobRoot strings');
    }

    if (jobRoots.length === 0) {
      json
        ? printJSON({ status: 'error', error: 'No jobRoots found in file' })
        : spinner.warn(themed(`Warning: ${themed('No jobRoots found in file')}.`, 'secondary'));
      return;
    }

    // Validate that all items are strings
    for (let i = 0; i < jobRoots.length; i++) {
      if (typeof jobRoots[i] !== 'string') {
        throw new Error(`JobRoot at index ${i} must be a string, got ${typeof jobRoots[i]}`);
      }
    }

    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // 2. Group jobRoots by tenant/flow
    // JobRoot format: {tenantId}/flows/{flowId}/...
    const groupedJobs = {};
    
    for (const jobRoot of jobRoots) {
      const parts = jobRoot.split('/');
      if (parts.length < 3 || parts[1] !== 'flows') {
        throw new Error(`Invalid jobRoot format: ${jobRoot}. Expected format: {tenantId}/flows/{flowId}/...`);
      }
      
      const tenantId = parts[0];
      const flowId = parts[2];
      const key = `${tenantId}/flows/${flowId}`;
      
      if (!groupedJobs[key]) {
        groupedJobs[key] = {
          tenant: tenantId,
          flow: flowId,
          jobRoots: [],
        };
      }
      
      groupedJobs[key].jobRoots.push(jobRoot);
    }

    const groupKeys = Object.keys(groupedJobs);
    !json && pr(
      themed(`Found ${themed(String(jobRoots.length), 'info')} jobRoot(s) grouped into ${themed(String(groupKeys.length), 'info')} tenant/flow group(s).`, 'secondary')
    );

    // 3. Helper function to wait for job completion
    const waitForJobCompletion = async (tenant, flow, jobRoot, maxWaitTime = 3600000) => { // 1 hour default
      const startTime = Date.now();
      const pollInterval = 5000; // Poll every 5 seconds
      
      while (Date.now() - startTime < maxWaitTime) {
        try {
          const jobStatus = await getJobStatus({
            debug,
            baseUri: clientApiBaseUri,
            env,
            apikey,
            flow,
            user_id: tenant,
            job_root: jobRoot,
          });
          
          const status = jobStatus.status || jobStatus.state;
          
          if (status === 'JOB_COMPLETED' || (typeof status === 'string' && status.endsWith('_FAILED'))) {
            return { completed: true, status };
          }
          
          // Wait before next poll
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        } catch (error) {
          debug(`Error checking job status for ${jobRoot}:`, error);
          // Continue polling even if there's an error
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
      }
      
      return { completed: false, status: 'TIMEOUT' };
    };

    // 4. Retrigger jobs: parallel across groups, sequential within groups
    message = themed(`Retriggering ${themed(String(jobRoots.length), 'info')} job(s) across ${themed(String(groupKeys.length), 'info')} group(s)...`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    const allResults = [];

    // Process all groups in parallel
    await Promise.all(
      groupKeys.map(async (key) => {
        const group = groupedJobs[key];
        const { tenant, flow, jobRoots: groupJobRoots } = group;
        const groupResults = [];

        // Process jobs sequentially within this group
        for (let i = 0; i < groupJobRoots.length; i++) {
          const jobRoot = groupJobRoots[i];
          
          try {
            // Retrigger the job
            const response = await retriggerJob({
              debug,
              baseUri: clientApiBaseUri,
              env,
              apikey,
              tenant,
              flow,
              job_root: jobRoot,
            });

            // Wait for job completion before retriggering the next one
            if (i < groupJobRoots.length - 1) {
              if (!json) {
                spinner.text = themed(`Waiting for job ${i + 1}/${groupJobRoots.length} in group ${tenant}/${flow} to complete...`, 'secondary');
              }
              const waitResult = await waitForJobCompletion(tenant, flow, jobRoot);
              
              if (!waitResult.completed) {
                debug(`Warning: Job ${jobRoot} did not complete within timeout period`);
              }
            }

            groupResults.push({
              tenant,
              flow,
              jobRoot,
              ok: true,
              response,
            });
          } catch (error) {
            let errorResult;
            
            if (error.response) {
              const statusCode = error.response.status;
              const statusText = error.response.statusText;
              const responseData = error.response.data;
              const message = error.message;
              
              debug('Error response:', {
                statusCode,
                statusText,
                message,
                data: responseData
              });
              
              errorResult = {
                statusCode,
                statusText,
                message,
                data: responseData
              };
            } else if (error.request) {
              debug('Error: No response received', error.message);
              errorResult = {
                message: error.message,
                request: error.request
              };
            } else {
              debug('Error:', error.message);
              errorResult = {
                message: error.message
              };
            }

            groupResults.push({
              tenant,
              flow,
              jobRoot,
              ok: false,
              error: errorResult,
            });
          }
        }

        allResults.push(...groupResults);
      })
    );

    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // 5. Print results
    if (json) {
      printJSON(allResults);
    } else {
      // Generate results table
      const table = new Table({
        head: ['Tenant', 'Flow', 'Job Root', 'Status', 'Response/Error'],
      });

      allResults.forEach((result) => {
        const status = result.ok ? 'SUCCESS' : 'FAILED';
        const responseOrError = result.ok
          ? JSON.stringify(result.response, undefined, 2)
          : JSON.stringify(result.error, undefined, 2);
        table.push([
          result.tenant || 'N/A',
          result.flow || 'N/A',
          result.jobRoot || 'N/A',
          status,
          responseOrError,
        ]);
      });

      console.log(table.toString());

      // Print summary
      const successCount = allResults.filter((r) => r.ok).length;
      const failCount = allResults.filter((r) => !r.ok).length;
      pr(
        themed(
          `Summary: ${themed(String(successCount), 'info')} succeeded, ${themed(String(failCount), 'error')} failed out of ${themed(
            String(allResults.length),
            'info'
          )} total.`,
          'secondary'
        )
      );
    }
  } catch (err) {
    if (json) {
      printJSON({ status: 'error', error: err.message, stack: err.stack });
    } else {
      spinner.fail(themed(`Error: ${message || 'Failed to retrigger jobs'}.`, 'secondary'));
      pr(themed(`Message: ${themed(err.message)}`, 'secondary'));
      debug(err);
    }
    process.exit(1);
  }
};

export { command, desc, builder, handler };
